import type { JSONContent } from '@tiptap/core';
import Color from '@tiptap/extension-color';
import Link from '@tiptap/extension-link';
import { TextStyle as BaseTextStyle } from '@tiptap/extension-text-style';
import type { MouseEvent as ReactMouseEvent } from 'react';

const FontSizeTextStyle = BaseTextStyle.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      fontSize: {
        default: null,
        parseHTML: (element: HTMLElement) => element.style.fontSize || null,
        renderHTML: (attributes: Record<string, unknown>) => {
          if (!attributes['fontSize']) {
            return {};
          }
          return { style: `font-size: ${attributes['fontSize']}` };
        },
      },
    };
  },
});
import Underline from '@tiptap/extension-underline';
import { generateHTML, generateJSON } from '@tiptap/html';
import StarterKit from '@tiptap/starter-kit';
import { sanitizeRichTextContent } from '@course-authoring/shared-types';

type HtmlGenerationExtensions = Parameters<typeof generateHTML>[1];

export function createRichTextRendererExtensions() {
  return [
    StarterKit.configure({
      heading: false,
      codeBlock: false,
      code: false,
      underline: false,
      link: false,
    }),
    Underline,
    Link.configure({
      openOnClick: true,
      autolink: false,
    }),
    FontSizeTextStyle,
    Color,
  ];
}

export type RichTextRendererExtensions = ReturnType<
  typeof createRichTextRendererExtensions
>;

/**
 * Accepts only a parseable, fully-qualified absolute URL over http(s).
 *
 * Shared by the rich-text link handler below and PolicyAcknowledgementRenderer's
 * policyUrl gate, so the two cannot drift apart.
 */
export function isSafeExternalUrl(url: string): boolean {
  try {
    // No base URL — requires a fully qualified absolute URL with protocol
    const parsed = new URL(url);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch {
    return false;
  }
}

// Matches NA-378's authoring-side validation: a mailto: with an actual address.
// A bare `mailto:` falls through to the blocked tier, as it does in the dialog.
const MAILTO_WITH_ADDRESS = /^mailto:[^\s@]+@[^\s@]+\.[^\s@]+/i;

/**
 * Delegated click handler for links inside rendered rich text.
 *
 * Rich text reaches the DOM via dangerouslySetInnerHTML, so its anchors are inert
 * markup rather than React elements — this runs on the container and resolves the
 * anchor from the event target.
 *
 * Three protocol tiers:
 *   http(s)  -> preventDefault, open a sized popup (matches PolicyAcknowledgementRenderer)
 *   mailto:  -> pass straight through to the browser, untouched
 *   anything else, malformed, relative, empty -> blocked, nothing opened
 */
export function handleRichTextLinkClick(
  event: ReactMouseEvent<HTMLElement>
): void {
  const target = event.target as Element | null;
  if (!target || typeof target.closest !== 'function') {
    return;
  }

  const anchor = target.closest('a');
  // Ignore anchors that escaped this container (shouldn't happen, but the
  // handler must never act on something it doesn't own).
  if (!anchor || !event.currentTarget.contains(anchor)) {
    return;
  }

  // Read the authored attribute rather than the resolved `.href` property:
  // `.href` would expand a relative path like `/handbook` into an absolute
  // same-origin URL, sneaking it past isSafeExternalUrl.
  const href = anchor.getAttribute('href')?.trim() ?? '';

  // Pass-through tier (NA-378). Checked before the popup path: a mailto handed
  // to window.open with width/height leaves a stray blank window behind — the
  // exact outcome NA-378 avoided by omitting target="_blank" on mailto links.
  if (MAILTO_WITH_ADDRESS.test(href)) {
    return;
  }

  // Validation is unconditional and runs before the modifier check — safety has
  // no modifier-key exemption.
  if (!isSafeExternalUrl(href)) {
    event.preventDefault();
    return;
  }

  // A deliberate modifier or non-primary click is the learner asking for a
  // specific browser behavior; don't override it.
  if (
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return;
  }

  // `noopener`/`noreferrer` are deliberately absent from the feature string:
  // per the HTML spec, `noopener` forces window.open to return null whether or
  // not the window opened, which would defeat the blocked-popup detection below.
  // `win.opener = null` gives the same isolation guarantee instead.
  // Do not "fix" this back to match PolicyAcknowledgementRenderer's call.
  const win = window.open(href, '_blank', 'width=800,height=600');

  if (win) {
    win.opener = null;
    event.preventDefault();
    return;
  }

  // Popup blocked. Leave the event alone so the anchor's own
  // target="_blank" rel="noopener noreferrer" opens an ordinary tab —
  // course content must never be a dead click.
}

export function createRichTextContentResetStyles(
  contentClassName: string,
  paragraphSpacing = '0.5em',
  reducedMotionSelectors: string[] = []
): string {
  const baseStyles = `
  .${contentClassName} p {
    margin: 0;
  }
  .${contentClassName} p:not(:last-child) {
    margin-bottom: ${paragraphSpacing};
  }
  .${contentClassName} ul,
  .${contentClassName} ol {
    margin: 0;
    padding-left: 2em;
  }
  .${contentClassName} li ul,
  .${contentClassName} li ol {
    padding-left: 1.25em;
  }
  .${contentClassName} ul:not(:last-child),
  .${contentClassName} ol:not(:last-child) {
    margin-bottom: ${paragraphSpacing};
  }
  .${contentClassName} li + li {
    margin-top: 0.25em;
  }
  .${contentClassName} ul { list-style-type: disc; }
  .${contentClassName} ul ul { list-style-type: circle; }
  .${contentClassName} ul ul ul { list-style-type: square; }
  .${contentClassName} ol { list-style-type: decimal; }
  .${contentClassName} ol ol { list-style-type: lower-alpha; }
  .${contentClassName} ol ol ol { list-style-type: lower-roman; }
  .${contentClassName} blockquote {
    border-left: 3px solid rgba(0, 0, 0, 0.2);
    padding-left: 1em;
    margin: 0.5em 0;
  }
  .${contentClassName} a {
    color: #4183C4;
    font-weight: normal;
    text-decoration: underline;
  }
  .${contentClassName} a:hover {
    opacity: 0.8;
  }`;

  if (reducedMotionSelectors.length === 0) {
    return baseStyles;
  }

  const reducedMotionRules = reducedMotionSelectors
    .map(
      (selector) => `    ${selector} {
      transition: none !important;
    }`
    )
    .join('\n');

  return `${baseStyles}
  @media (prefers-reduced-motion: reduce) {
${reducedMotionRules}
  }`;
}

export function safeGenerateRichTextHTML(
  content: JSONContent,
  extensions: HtmlGenerationExtensions
): string {
  try {
    return generateHTML(sanitizeRichTextContent(content), extensions);
  } catch {
    return '<p></p>';
  }
}

const EMPTY_DOC: JSONContent = { type: 'doc', content: [{ type: 'paragraph' }] };

export function safeParseHTMLToJSONContent(html: string): JSONContent {
  if (!html.trim()) return EMPTY_DOC;
  try {
    const extensions = createRichTextRendererExtensions();
    return generateJSON(html, extensions) as JSONContent;
  } catch {
    return EMPTY_DOC;
  }
}

export function extractPlainTextFromRichContent(content: JSONContent | null | undefined): string {
  if (!content || typeof content !== 'object') {
    return '';
  }

  if (content.type === 'text') {
    return content.text ?? '';
  }

  return (content.content ?? [])
    .map((child) => extractPlainTextFromRichContent(child))
    .join('')
    .trim();
}

function escapeHtml(text: string): string {
  return text
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function isEffectivelyEmptyRichTextHtml(html: string): boolean {
  return /^<p(?:\s+[^>]*)?>\s*<\/p>$/.test(html.trim());
}

export function safeGenerateNonEmptyRichTextHTML(
  content: JSONContent | null | undefined,
  extensions: HtmlGenerationExtensions
): string | null {
  if (!content) {
    return null;
  }

  const html = safeGenerateRichTextHTML(content, extensions);
  if (!isEffectivelyEmptyRichTextHtml(html)) {
    return html;
  }

  const fallbackText = extractPlainTextFromRichContent(content);
  if (!fallbackText) {
    return null;
  }

  return `<p>${escapeHtml(fallbackText)}</p>`;
}
